<template>
  <label for="name" class="block text-sm font-medium text-gray-900 dark:text-white">Select Content Type</label>
  <Multiselect
    v-if="!contentTypesLoading && !contentLoading"
    v-model="selectedContentType"
    class="mt-4"
    :allow-empty="false"
    :searchable="true"
    placeholder="Make selection"
    :options="contentTypeOptions"
    :object="true"
    label="name"
    value-prop="id" />

  <div v-if="contentTypesLoading || contentLoading" class="my-4" role="status">
    <svg
      aria-hidden="true"
      class="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
    </svg>
    <span class="sr-only">Loading...</span>
  </div>

  <div v-if="selectedContentType" class="py-2">
    <div v-for="[inputName, inputType] in selectedContentType.dynamic_inputs" :key="inputName">
      <!-- For 'single' type -->
      <div class="my-4">
        <label for="name" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ inputName }} (Dynamic)</label>
        <input
          v-if="inputType === 'single'"
          v-model="dynamicInputValues[inputName]"
          required
          type="text"
          :placeholder="inputName"
          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500">
      </div>

      <div class="my-4">
        <!-- For 'list' type -->
        <textarea
          v-if="inputType === 'list'"
          v-model="dynamicInputValues[inputName]"
          required
          :placeholder="inputName"
          class="my-2 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" />
      </div>
    </div>
  </div>

  <div class="mt-4" v-if="selectedContentType && selectedLocation">
    <a v-if="!contentLoading" href="/" class="px-5 mr-2 focus:outline-none text-white bg-blue-600 py-4 hover:bg-blue-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800" @click.prevent="createContent()">Generate Article</a>

    <button v-if="contentLoading" disabled type="button" class="px-5 mr-2 text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:outline-none focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 inline-flex items-center">
      <svg
        aria-hidden="true"
        role="status"
        class="inline w-4 h-4 mr-3 text-gray-200 animate-spin dark:text-gray-600"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="#1C64F2" />
      </svg>
      Loading...
    </button>
  </div>

  <div class="mt-4" v-if="selectedContentType">
    <a v-if="!contentLoading" href="/" class="px-5 mr-2 focus:outline-none text-white bg-blue-600 py-4 hover:bg-blue-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800" @click.prevent="createContent()">Generate</a>

    <button v-if="contentLoading" disabled type="button" class="px-5 mr-2 py-2.5 mr-2 mb-  text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:outline-none focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 inline-flex items-center">
      <svg
        aria-hidden="true"
        role="status"
        class="inline w-4 h-4 mr-3 text-gray-200 animate-spin dark:text-gray-600"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="#1C64F2" />
      </svg>
      Loading...
    </button>
  </div>
</template>

<script setup>
import axios from 'axios'
import {
  ref, watch, onMounted, defineProps
} from 'vue'
import { createToast } from 'mosha-vue-toastify'
import Multiselect from '@vueform/multiselect'

const props = defineProps({
  locationId: String,
  contentTypeId: String
})

onMounted(async() => {
  await getContentTypes()

  console.log(`selected content type: ${selectedContentType.value}`)
})

const contentTypeOptions = ref([])
const selectedContentType = ref('')
const contentTypesLoading = ref(true)
const dynamicInputValues = ref({})

const getContentTypes = () => {
  axios.get('/ajax/clients/ai_content/content_types').then((response) => {
    contentTypesLoading.value = false
    console.log(response.data.content_types)
    contentTypeOptions.value = response.data.content_types
    selectedContentType.value = contentTypeOptions.value.find((contentType) => contentType.id === props.contentTypeId)
  }).catch((error) => {
    console.log(error)
  })
}

const contentLoading = ref(false)

const createContent = () => {
  contentLoading.value = true
  createToast('Content creating... you will be redirected on completion.', { type: 'success' })
  const payload = {
    content_type_id: selectedContentType.value.id,
    location_id: props.locationId,
    dynamic_inputs: dynamicInputValues.value
  }

  axios.post('/ajax/clients/ai_content/content_entities/custom_generate', payload)
    .then((response) => {
      window.location = `/clients/ai_content/content_entities/${response.data.id}`
    })
    .catch((error) => {
      createToast(error.response.data.error, { type: 'danger' })
    })
    .finally(() => {
      contentLoading.value = false
    })
}
</script>
